import cookies from 'js-cookie';
import { isEmpty } from 'lodash';
import {
  actionChannel,
  all,
  call,
  delay,
  put,
  select,
  take,
  takeLatest,
  type SagaReturnType,
} from 'redux-saga/effects';

import type {
  CheckoutEvent,
  postStatsCheckoutRequest,
} from '@lt/api/dist/methods/stats/types';
import type { SearchType } from '@lt/api/dist/types/searchParams';

import {
  getIsHotelOnly,
  getIsMobile,
  searchTypeSelector,
} from 'src/store/view/selectors';

import type { SagaIterator } from 'redux-saga';
import { logger } from 'src/error';

// Constants:
import type { PostOrdersCreate } from '@lt/api/dist/methods/orders/types';
import { api } from 'src/api';
import {
  getClientBonusScore,
  getClientData,
  getClientStatsData,
} from 'src/store/client/selectors';
import { setError } from 'src/store/error/slice';
import { initPayment } from 'src/store/payment/actions';
import { resetPopupState, setPopupState } from 'src/store/popup/slice';
import { TOGGLE_FLIGHT_CLASS } from '../../CheckoutRoutes/PackageCheckout/store/flights/constants';
import { ACTUALIZE_PACKAGE_FAIL } from '../constants/actualization';
import {
  // Mock:
  MOCK_CLIENT,
  ORDER_CREATION_DEFAULT_ERROR,
} from '../constants/order';
import { REDIRECT_TO_ORDER_DELAY } from '../constants/view';

// Selectors:
import {
  confirmClientAction,
  resetClientAction,
  setClientBonusScore,
} from '../../../../store/client/actions';
import {
  getIsPriceChangedOnPay,
  getOrderBonusesGranted,
  getOrderCommentSerialized,
  getOrderCoupon,
  getOrderFlight,
  getOrderId,
  getOrderIsCreated,
  getOrderPin,
  getOrderTourists,
  getPackageFullPrice,
  getSelectedFlightData,
  getSource,
  isFirstOrderSelector,
  makeGetOrderFieldError,
  ordersAmountSelector,
} from '../selectors/order';
import {
  getIsManualPackage,
  getPackageId,
  getTourId,
} from '../selectors/package';

import {
  getDefaultPaymentMethod,
  getPaymentMethod,
} from '../../../../store/payment/selectors';
import { getMockTourists } from '../selectors/data';
import { originalSelectedExtrasSelector } from '../selectors/services';

import { PAYMENT_METHOD_CASH } from '../../../../store/payment/constants';

// ACTIONS:
// Payment Actions:
import {
  finishPayment,
  setOrderInfo,
  setPaymentAmount,
  setPaymentData,
  setPaymentMethod,
} from '../../../../store/payment/slice';
// Order
import {
  addBonusAction,
  addCouponAction,
  // Extras:
  addExtraAction,
  confirmOrderAction,
  // Create order:
  createOrderAction,
  createOrderFail,
  createOrderSuccess,
  finishOrderAction,
  goToOrderPageAction,
  payByDiscountAction,
  // Prebuild:
  prebuildOrderAction,
  prebuildOrderCompleteWithMindboxErrors,
  prebuildOrderFail,
  prebuildOrderSuccess,
  removeExtraAction,
  selectFlight as selectFlightAction,
  // Flights:
  setFlightInfoAction,
  // Flights:
  skipFlightsAction,
  stashUserPriceBeforePayAction,
  submitPackageAction,
  toggleExtraAction,
  // Update order:
  updateOrderAction,
  updateOrderFailedAction,
  updateOrderSuccessAction,
} from '../actions/order';
// View:
import { goToClientAction, goToPathAction } from '../actions/view';
// Actualization:
import {
  actualizePackageAction,
  actualizePackageSuccess,
  startActualizationProcessAction,
  stopActualizationProcessAction,
} from '../actions/actualization';

import { similarPackagesStartAction } from '../actions/similar-packages';

// Utils:
import { cheapestFlightOptionIdSelector } from '../../CheckoutRoutes/PackageCheckout/store/flights/selectors';
import { getActualizationShowModalCustomer } from '../selectors/actualization';

// Visa:
import { updateVisa } from '../domains/visa';

// Prebook
import { flightsSetAction } from '../../CheckoutRoutes/PackageCheckout/store/flights/slice';
import { clearTryCount } from '../domains/package-prebook/slice';

import { getBookingId } from '../domains/package-prebook';
import { getChosenOptions } from '../domains/package-upsale-options/selectors';

import { HAS_CUSTOMER_FORM } from '../../CheckoutRoutes/PackageCheckout/components/Customer/abFeatures';
import { SUCCESS_PATH } from '../constants';
import { EXTRA_SCOPES } from '../constants/extras';
import { customerInfoSelectorTransformed } from '../domains/customer/selectors';
import { visaDTO } from '../domains/visa/visaDTO';
import { createOrderSuccessDTO } from '../order/createOrderSuccessDTO';
import { updateOrderSuccessDTO } from '../order/updateOrderSuccessDTO';
import { getPartnerLoyaltyProgramCardNumber } from '../partnerLoyalty/selectors';
import { setEmptyTouristsAction } from '../actions/tourists';

/**
 * Собирает и возвращает данные по заказу
 * @returns Данные по заказу
 */
export function* getOrderData() {
  const packageId: SagaReturnType<typeof getPackageId> = yield select(
    getPackageId,
  );
  const isManualPackage: SagaReturnType<typeof getIsManualPackage> =
    yield select(getIsManualPackage);
  const isHotelOnly: SagaReturnType<typeof getIsHotelOnly> = yield select(
    getIsHotelOnly,
  );
  const clientData: SagaReturnType<typeof getClientData> = yield select(
    getClientData,
  );
  const touristsData: SagaReturnType<typeof getOrderTourists> = yield select(
    getOrderTourists,
  );
  const flightId: SagaReturnType<typeof getOrderFlight> = yield select(
    getOrderFlight,
  );
  const cheapestId: SagaReturnType<typeof cheapestFlightOptionIdSelector> =
    yield select(cheapestFlightOptionIdSelector);
  const flightPairId = flightId || cheapestId;
  const paymentOption: SagaReturnType<typeof getPaymentMethod> = yield select(
    getPaymentMethod,
  );
  const defaultPaymentMethod: SagaReturnType<typeof getDefaultPaymentMethod> =
    yield select(getDefaultPaymentMethod);

  const selectedExtras = yield select(originalSelectedExtrasSelector);
  const couponCode: SagaReturnType<typeof getOrderCoupon> = yield select(
    getOrderCoupon,
  );
  const bonusesGranted: SagaReturnType<typeof getOrderBonusesGranted> =
    yield select(getOrderBonusesGranted);
  const comment: SagaReturnType<typeof getOrderCommentSerialized> =
    yield select(getOrderCommentSerialized);
  const jointOrdersAmount: SagaReturnType<typeof ordersAmountSelector> =
    yield select(ordersAmountSelector);
  const isFirstOrderInUnited: SagaReturnType<typeof isFirstOrderSelector> =
    yield select(isFirstOrderSelector);
  const upsaleOptionsHashsums: SagaReturnType<typeof getChosenOptions> =
    yield select(getChosenOptions);
  const partnerLoyaltyProgramCardNumber: SagaReturnType<
    typeof getPartnerLoyaltyProgramCardNumber
  > = yield select(getPartnerLoyaltyProgramCardNumber);
  const bookingId: SagaReturnType<typeof getBookingId> = yield select(
    getBookingId,
  );
  // TODO feature customer_form - удалить customerData
  const customerData: SagaReturnType<typeof customerInfoSelectorTransformed> =
    yield select(customerInfoSelectorTransformed);

  if (!flightPairId && !isManualPackage && !isHotelOnly) {
    throw new Error('Должен быть выбран перелет');
  }

  const client = {
    email: clientData?.email || MOCK_CLIENT.email,
    id: clientData?.id || undefined,
    name: clientData?.name || MOCK_CLIENT.name,
    surname: clientData?.surname || MOCK_CLIENT.surname,
    phone: clientData?.phone || MOCK_CLIENT.phone,
  };
  const showModalCustomer = yield select(getActualizationShowModalCustomer);

  const data = {
    package_id: packageId,
    client,
    tourists: touristsData || (yield select(getMockTourists)),
    payment_option: paymentOption || defaultPaymentMethod,
    flight_pair_id: flightPairId,
    selected_extras: [
      ...selectedExtras.map((extra) => extra.hashsum),
      ...upsaleOptionsHashsums,
    ],
    coupon_code: couponCode,
    bonuses_granted: bonusesGranted || 0,
    comment,
    partner_loyalty_program_card_number: partnerLoyaltyProgramCardNumber,
    booking_id: bookingId,
    united_orders_counter: isFirstOrderInUnited ? jointOrdersAmount : null,
    customer: HAS_CUSTOMER_FORM && showModalCustomer ? customerData : null,
  };

  return data;
}

/**
 * Собирает информацию из кук и channel tracker'a
 */
function* getAdditionalParams() {
  const source: SagaReturnType<typeof getSource> = yield select(getSource);
  return {
    source,
    firstinteraction: cookies.get('firstinteraction') || null,
    last_cookie_link: cookies.get('last_cookie_link') || null,
    utmz: cookies.get('__utmz') || null,
    lt_ref: cookies.get('lt_ref') || null,
    uuid: cookies.get('uuid') || null,
    mrint: cookies.get('mrint') || null,
    channel_tracks: window._cht?.read('json') || null,
  };
}

/**
 * Когда отработала сага актуализации и вызвала сагу полетов, то ждем,
 * пока в стор положатся полеты, поcле чего вызываем пребилд
 */
function* handleActualizationSuccess() {
  const isHotelOnly: SagaReturnType<typeof getIsHotelOnly> = yield select(
    getIsHotelOnly,
  );
  if (!isHotelOnly) {
    yield take(flightsSetAction.type);
  }

  yield put(prebuildOrderAction());
}

/**
 * Собирает данные заказа и отправляет на перерасчет
 */
function* handlePrebuild({
  type,
  payload,
}: {
  type: string;
  payload: PostOrdersCreate;
}) {
  try {
    const orderData: SagaReturnType<typeof getOrderData> = yield getOrderData();
    const additionalParams = yield getAdditionalParams();
    const isAfterSuccessActualization = type === actualizePackageSuccess.type;
    const orderParams = isAfterSuccessActualization
      ? orderData
      : payload || orderData;

    const response: SagaReturnType<typeof api.postOrdersPrebuild> =
      yield api.postOrdersPrebuild({
        ...orderParams,
        ...additionalParams,
      });

    const { success, orderErrors } = response;

    if (success) {
      yield put(prebuildOrderSuccess(response));
      yield put(updateVisa(visaDTO(response.package.hotel.visa)));
      yield put(
        setPaymentData({
          methods: response.paymentMethods,
          ofertaText: response.ofertaText,
        }),
      );
    } else {
      const clientCashback: SagaReturnType<typeof getClientBonusScore> =
        yield select(getClientBonusScore);

      // Баланс может измениться из-за особенностей синхронизации с mindbox
      if (response.client.bonus_score !== clientCashback) {
        yield put(
          setClientBonusScore({
            bonus_score: response.client.bonus_score,
          }),
        );
      }

      if (
        orderErrors?.base?.[0] === 'balance_changed' ||
        orderErrors?.base?.[0] === 'loyalty_not_available'
      ) {
        yield put(
          prebuildOrderCompleteWithMindboxErrors({ ...response, orderErrors }),
        );
        yield put(updateVisa(visaDTO(response.package.hotel.visa)));
        yield put(
          setPaymentData({
            methods: response.paymentMethods,
            ofertaText: response.ofertaText,
          }),
        );
      } else {
        yield put(prebuildOrderFail(orderErrors ?? {}));
      }

      const flightData: SagaReturnType<typeof getSelectedFlightData> =
        yield select(getSelectedFlightData);
      const orderFlight: SagaReturnType<typeof getOrderFlight> = yield select(
        getOrderFlight,
      );

      const isSelectedFlightChanged = orderFlight && !flightData;

      //  Вызываем симилар, если выбранный перелет перестал существовать
      if (isSelectedFlightChanged) {
        yield put(similarPackagesStartAction());
      }
    }
  } catch (error) {
    logger(error, 'Prebuild');
    if (error instanceof Error) {
      yield put(prebuildOrderFail({ uncaught: [error.message] }));
    }
  }
}

function* handlePrebuildFail() {
  const getOrderFieldError = makeGetOrderFieldError('package_id');
  const isPackageOrdered: SagaReturnType<typeof getOrderFieldError> =
    yield select(getOrderFieldError);

  // если пакет уже куплен, то нужно получить новый пакет
  if (isPackageOrdered) {
    yield put(similarPackagesStartAction());
  }
}

/**
 * Обрабатывает добавление доп услуг:
 * @param {Action} action ADD_EXTRAS;
 */
function* handleAddExtra(action) {
  const { payload: extraHash } = action;
  const orderParams: SagaReturnType<typeof getOrderData> = yield getOrderData();
  const selected_extras = [...orderParams.selected_extras, extraHash];
  const requestParams = { ...orderParams, selected_extras };
  yield put(prebuildOrderAction(requestParams));
}

/**
 * Обрабатывает переключение доп услуг:
 * @param {Action} action ADD_EXTRAS;
 */
function* handleToggleExtra(action: ReturnType<typeof toggleExtraAction>) {
  const {
    payload: { hashsum, selected },
  } = action;
  const handler = selected ? addExtraAction : removeExtraAction;
  yield put(handler(hashsum));
}

/**
 * Обрабатывает удаление доп услуг:
 * @param {Action} action ADD_EXTRAS;
 */
function* handleRemoveExtra(action) {
  const { payload: extraHash } = action;
  const orderParams = yield getOrderData();
  const idxToRemove = orderParams.selected_extras.indexOf(extraHash);
  if (idxToRemove >= 0) {
    const selected_extras = orderParams.selected_extras.filter(
      (v, idx) => idx !== idxToRemove,
    );
    const requestParams = { ...orderParams, selected_extras };
    yield put(prebuildOrderAction(requestParams));
  }
}

/**
 * Обработка выбора рейса
 * @param {Action} action
 */
function* selectFlight(action) {
  const { extras = [] } = action.payload;

  try {
    const flightData: SagaReturnType<typeof getSelectedFlightData> =
      yield select(getSelectedFlightData);

    if (!flightData) {
      throw new Error('Can not get flight data');
    }
    yield put(
      setFlightInfoAction({
        to: flightData.to.departure,
        back: flightData.back[0].departure,
        business: flightData.to.business,
      }),
    );
    const isCharter = Boolean(flightData.back[0].warning);
    window.FLIGHTS = flightData && { ...flightData, isCharter };
  } catch (error) {
    logger(error, 'Flight');
  }

  if (action.type === selectFlightAction.type) {
    yield put(clearTryCount());
    yield put(goToClientAction());
  }
  const orderParams = yield getOrderData();
  const flightExtras = extras
    .filter((extra) => extra.scope === EXTRA_SCOPES.FLIGHT)
    .map((extra) => extra.hashsum);
  const currentExtras = yield select(originalSelectedExtrasSelector);
  const selected_extras = [
    ...currentExtras
      .filter((extra) => extra.scope !== EXTRA_SCOPES.FLIGHT)
      .map((extra) => extra.hashsum),
    ...flightExtras,
  ];

  const requestParams = { ...orderParams, selected_extras };
  yield put(prebuildOrderAction(requestParams));
}

/**
 * Если пакет создан вручную из менеджерки, пропускаем секцию выбора перелета
 */
function* skipFlights() {
  yield put(prebuildOrderAction());
  yield put(setEmptyTouristsAction());
  yield put(goToClientAction());
}

function* submitClient() {
  yield put(prebuildOrderAction());
}

/**
 * Обработка платежа uniteller
 * Собирает данные и оформляет заказ
 */
function* buildOrder() {
  yield put(stopActualizationProcessAction());
  yield put(setPopupState('fetching'));
  try {
    const orderData = yield getOrderData();
    const additionalParams = yield getAdditionalParams();

    const response = yield api.postOrdersСreate({
      ...orderData,
      ...additionalParams,
    });

    const { success, status, order, order_errors } = response || {};
    if (success) {
      const { id, secret_stamp: pin, payment_methods } = order;
      const paymentMethod: SagaReturnType<typeof getPaymentMethod> =
        yield select(getPaymentMethod);
      yield put(
        setPaymentAmount(
          payment_methods.find((method) => method.type === paymentMethod)
            ?.amount ?? null,
        ),
      );
      yield put(setOrderInfo({ orderId: id, pin }));
      yield put(createOrderSuccess(createOrderSuccessDTO(order)));
    } else {
      const message = order_errors || ORDER_CREATION_DEFAULT_ERROR;
      yield* handleCreateOrderFail({
        message,
        analyticsError: `${status}: ${message}`,
      });
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    logger(error, ORDER_CREATION_DEFAULT_ERROR);
    const errorResponse = 'response' in error ? error.response : {};
    const { status, data } = errorResponse;

    yield* handleCreateOrderFail({
      message: ORDER_CREATION_DEFAULT_ERROR,
      analyticsError: `${status}: ${data?.error || 'Unknown server error'}`,
    });
  }
}

function* handleOrderCreated() {
  yield put(initPayment());
}

function* sendOrderSuccessedAnalytics() {
  const paramsFromStore: SagaReturnType<typeof getStatsCheckoutData> =
    yield getStatsCheckoutData('order_creation_successed');

  if (!paramsFromStore) {
    logger(
      paramsFromStore,
      'Invalid params for order creation success analytics',
    );
    return;
  }

  try {
    const response: SagaReturnType<typeof api.postStatsCheckout> =
      yield api.postStatsCheckout(paramsFromStore);
    const { status, success } = response || {};

    if (!success) {
      logger(status, 'StatsCheckout handleSubmitOrder');
    }
  } catch (error) {
    logger(error, 'StatsCheckout handleSubmitOrder');
  }
}

/**
 * Отправляет события ошибки создания или оплаты заказа
 * на бэкенд
 */
function* sendOrderFailAnalytics(error) {
  try {
    const eventName = 'order_creation_failed';
    const analyticsParamsFromStore: SagaReturnType<
      typeof getStatsCheckoutData
    > = yield getStatsCheckoutData(eventName);
    if (!analyticsParamsFromStore) {
      logger(
        analyticsParamsFromStore,
        'Invalid params for order creation fail analytics',
      );
      return;
    }
    const errorMessage =
      'analyticsError' in error ? error.analyticsError : error.message;

    const response: SagaReturnType<typeof api.postStatsCheckout> =
      yield api.postStatsCheckout({
        ...analyticsParamsFromStore,
        error: errorMessage,
      });
    const { status, success } = response || {};

    if (!success) {
      logger(status, 'sendOrderFailAnalytics');
    }
  } catch (e) {
    logger(e, 'sendOrderFailAnalytics');
  }
}

/**
 * Собирает и возвращает данные для отправки события ошибки
 * создания заказа на бэкенд
 */
export function* getStatsCheckoutData(
  event: CheckoutEvent,
): SagaIterator<postStatsCheckoutRequest | null> {
  const client: SagaReturnType<typeof getClientStatsData> = yield select(
    getClientStatsData,
  );
  const tourists = yield select(getOrderTourists);

  const package_id: SagaReturnType<typeof getPackageId> = yield select(
    getPackageId,
  );
  const tour_id = yield select(getTourId);
  const flight_pair_id = yield select(getOrderFlight);
  const partner_id = window.PARTNER ? window.PARTNER.id : null;
  const isMobile: SagaReturnType<typeof getIsMobile> = yield select(
    getIsMobile,
  );
  const searchType: SearchType = yield select(searchTypeSelector);

  const eventName: postStatsCheckoutRequest['event'] = `${event}_${searchType}`;

  if (!client.id) throw new Error('Invalid client data');

  if (!tourists) return null;

  return {
    client: {
      id: client.id,
      uuid: client.uuid,
      email: client.email || undefined,
      phone: client.phone || undefined,
    },
    event: eventName,
    package_id,
    tour_id,
    flight_pair_id,
    tourists,
    is_mobile: isMobile,
    url: window.location.href,
    partner_id,
  };
}

function* handleCreateOrderFail(error) {
  /**
   * Заново запускает актуализацию при фейле создания заказа
   */
  yield put(startActualizationProcessAction());
  // Показать попап
  yield put(setPopupState('error'));
  // Техническая аналитика
  yield sendOrderFailAnalytics(error);
  // Сообщение об ошибке
  yield put(setError({ message: ORDER_CREATION_DEFAULT_ERROR }));
  // Остановить создание заказа
  yield put(createOrderFail());
}

/**
 * Переключаем метод оплаты
 */
function* handleChangePayment() {
  const isCreated: SagaReturnType<typeof getOrderIsCreated> = yield select(
    getOrderIsCreated,
  );

  if (!isCreated) {
    yield put(prebuildOrderAction());
  }
}

/**
 * Добавление купона
 */
function* handleAddCoupon(action) {
  const { payload: coupon_code } = action;
  const orderParams = yield getOrderData();
  const requestParams = { ...orderParams, coupon_code, bonuses_granted: '' };
  yield put(prebuildOrderAction(requestParams));
}

/**
 * Добавление бонусных очков
 */
function* handleAddBonus(action) {
  const { payload: bonuses_granted } = action;
  const orderParams = yield getOrderData();
  const requestParams = { ...orderParams, bonuses_granted, coupon_code: '' };
  yield put(prebuildOrderAction(requestParams));
}

/**
 * Хак: если заказ полностью оплачивается подарочным сертификатом,
 */
function* handlePayByGiftCert() {
  // TODO: это хак: если заказ полностью оплачивается подарочным сертификатом,
  // ставим оплату наличными.
  yield put(setPaymentMethod(PAYMENT_METHOD_CASH));
  yield put(submitPackageAction());
}

/**
 * Переброс на страницу окончательного заказа
 */
function* goToOrderPage() {
  const id: SagaReturnType<typeof getOrderId> = yield select(getOrderId);
  const pin: SagaReturnType<typeof getOrderPin> = yield select(getOrderPin);
  const redirectUrl = `/orders/${id}?pin=${pin}`;
  window.location.href = redirectUrl;
}

/**
 * Перекидывает на страницу заказа через 15 секунд
 */
function* handleFinishOrder() {
  yield put(resetPopupState());
  yield put(goToPathAction(SUCCESS_PATH));
  window.scrollTo(0, 0);
  yield put(finishOrderAction());
  yield delay(REDIRECT_TO_ORDER_DELAY);
  yield put(goToOrderPageAction());
}

/**
 * Отправка заказа созданного вручную
 */
export function* handleSubmitManual() {
  const isCreated: SagaReturnType<typeof getOrderIsCreated> = yield select(
    getOrderIsCreated,
  );
  if (!isCreated) {
    yield put(createOrderAction());
  } else {
    yield handleOrderCreated();
  }
}

/**
 * Отправка заказа с проверкой актуальности цены
 */
export function* handleSubmitWithUpdate() {
  const isCreated: SagaReturnType<typeof getOrderIsCreated> = yield select(
    getOrderIsCreated,
  );
  const currentPrice: SagaReturnType<typeof getPackageFullPrice> = yield select(
    getPackageFullPrice,
  );

  yield put(stashUserPriceBeforePayAction(currentPrice));

  if (!isCreated) {
    // Вызываем актуализацию, если заказ еще не был создан
    yield put(actualizePackageAction({ force: true }));
    // TODO: сформулировать порядок дейсьвий при фейле актуалайза.
    yield take([
      prebuildOrderSuccess,
      prebuildOrderFail,
      ACTUALIZE_PACKAGE_FAIL,
    ]);

    const isPriceChanged: SagaReturnType<typeof getIsPriceChangedOnPay> =
      yield select(getIsPriceChangedOnPay);
    if (!isPriceChanged) {
      yield put(createOrderAction());
    }
  } else {
    yield put(updateOrderAction());
    yield take([updateOrderSuccessAction, updateOrderFailedAction]);
    const isPriceChanged: SagaReturnType<typeof getIsPriceChangedOnPay> =
      yield select(getIsPriceChangedOnPay);
    if (!isPriceChanged) {
      yield handleOrderCreated();
    }
  }
}

function* handleUpdateOrder() {
  try {
    const id: SagaReturnType<typeof getOrderId> = yield select(getOrderId);
    const pin: SagaReturnType<typeof getOrderPin> = yield select(getOrderPin);

    const response = yield api.getOrdersShow({ id: id || 0, pin });

    const { success, order } = response || {};
    if (success && !isEmpty(order)) {
      yield put(updateOrderSuccessAction(updateOrderSuccessDTO(order)));
    } else {
      yield put(updateOrderFailedAction());
    }
  } catch (error) {
    yield put(updateOrderFailedAction());
  }
}

function* handleOrderConfirmed() {
  const isOrderCreated: SagaReturnType<typeof getOrderIsCreated> = yield select(
    getOrderIsCreated,
  );
  if (isOrderCreated) {
    yield handleOrderCreated();
  }
}

export function* watchPrebuildOrderRequest() {
  // создаем канал для запросов по определенному action
  const requestChan = yield actionChannel(prebuildOrderAction);
  while (true) {
    // берем action из канала
    const action = yield take(requestChan);
    // и именно при помощи call вызываем (fork нельзя потому что будет асинхронно)
    yield call(handlePrebuild, action);
  }
}

export default function* checkoutOrderWatcher() {
  yield all([
    takeLatest(actualizePackageSuccess, handleActualizationSuccess),
    takeLatest(prebuildOrderFail, handlePrebuildFail),

    // Sub checks
    takeLatest(confirmClientAction, submitClient),
    takeLatest(resetClientAction, submitClient),

    // Extras:
    takeLatest(addExtraAction, handleAddExtra),
    takeLatest(toggleExtraAction, handleToggleExtra),
    takeLatest(removeExtraAction, handleRemoveExtra),

    // Flights:
    takeLatest([selectFlightAction, TOGGLE_FLIGHT_CLASS], selectFlight),
    takeLatest(skipFlightsAction, skipFlights),

    // Updated order:
    takeLatest(updateOrderAction, handleUpdateOrder),

    // Changin payment method:
    takeLatest(setPaymentMethod, handleChangePayment),

    // Create order:
    takeLatest(createOrderAction, buildOrder),
    takeLatest(createOrderSuccess, handleOrderCreated),
    takeLatest(createOrderSuccess, sendOrderSuccessedAnalytics),

    // Payments:
    takeLatest(addCouponAction, handleAddCoupon),
    takeLatest(addBonusAction, handleAddBonus),
    takeLatest(payByDiscountAction, handlePayByGiftCert),

    // Order process:
    takeLatest(finishPayment, handleFinishOrder),
    takeLatest(goToOrderPageAction, goToOrderPage),
    takeLatest(confirmOrderAction, handleOrderConfirmed),
  ]);
}
